import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Popover,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { visuallyHidden } from '@mui/utils';
import { GridColDef, GridRenderCellParams, GridSearchIcon } from '@mui/x-data-grid';
import { IconEye, IconPencil } from '@tabler/icons';
import useAuth from 'hooks/useAuth';
import useFormBuilderTemplate from 'hooks/useFormBuilderTemplate';
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state';
import moment from 'moment';
import { useModal } from 'mui-modal-provider';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { store } from 'store';
import { RESET_FIELDS, RESET_FORM_ID, SET_CREATE_FORM_FIELDS, SET_ITEMS, SET_STEPS, SET_TEMPLATE_ID } from 'store/actions';
import { getComparator } from 'utils/table';
import NoResults from '../common/NoResults';
import CreateForm from '../modals/CreateForm';
import PopularCard from '../skeleton/PopularCard';
import PreviewFormBuilder from './PreviewFormBuilder';

const useStyles = makeStyles({
  root: {
    width: '100%',
    overflow: 'hidden'
  },
  container: {
    maxHeight: 'calc(100vh - 352px)'
  }
});

const TemplatesTable = () => {
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles();
  const intl = useIntl();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const { userTemplates, userTemplatesIsLoading, updateTemplate, refetchUserTemplates } = useFormBuilderTemplate({ userId });
  const { showModal } = useModal();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formBuilderTemplates, setFormBuilderTemplates] = useState(
    userTemplates?.slice(page * rowsPerPage, (page + 1) * rowsPerPage) || []
  );
  const [orderBy, setOrderBy] = useState<any>('createdAt');
  const [order, setOrder] = useState<any>('desc');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    refetchUserTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleRequestSort = (_: React.MouseEvent<unknown>, property: any) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property: any) => (event: React.MouseEvent<unknown>) => {
    handleRequestSort(event, property);
  };
  const handleAdd = useCallback(
    (e, templateData) => {
      if (templateData) {
        store.dispatch({ type: SET_TEMPLATE_ID, payload: templateData?._id });
        store.dispatch({ type: RESET_FORM_ID });
        const createFormFields = {
          name: templateData?.name,
          nameFrench: templateData?.nameFrench,
          description: templateData?.description,
          descriptionFrench: templateData?.descriptionFrench,
          isForNotes: templateData?.isForNotes,
          lng: templateData?.lng,
          templateAvailableTo: {
            providerAdministratorD: templateData?.templateAvailableTo?.providerAdministratorD,
            providerAdministratorS: templateData?.templateAvailableTo?.providerAdministratorS,
            provider: templateData?.templateAvailableTo?.provider,
            administrator: templateData?.templateAvailableTo?.administrator
          },
          type: templateData?.type
        };
        store.dispatch({ type: SET_CREATE_FORM_FIELDS, payload: createFormFields });
        store.dispatch({
          type: SET_ITEMS,
          payload: templateData?.type === 'default' ? templateData?.items : templateData?.steps?.length ? templateData?.steps[0]?.items : []
        });
        store.dispatch({ type: SET_STEPS, payload: templateData?.steps ?? [{ title: 'Title', items: [] }] });
      } else {
        store.dispatch({ type: RESET_FIELDS });
      }
      const modal = showModal(CreateForm, {
        open,
        onClose: async () => {
          modal.hide();
          setOpen(false);
          await refetchUserTemplates();
        },
        onCancel: async () => {
          modal.hide();
          setOpen(false);
        },
        isFromTemplate: true,
        templateId: templateData?._id,
        isEditing: templateData ? true : false
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showModal, refetchUserTemplates]
  );
  const actions = ['Archive'];
  const [searchString, setSearchString] = useState('');
  const handleStatusToggle = async (row) => {
    const updateData = { ...row };
    delete updateData?._id;
    delete updateData?.user;
    delete updateData?.status;
    delete updateData?.archive;
    delete updateData?.createdAt;
    delete updateData?.updatedAt;
    delete updateData?.__v;
    delete updateData?.clinicName;
    delete updateData?.clientResponses;
    await updateTemplate({ payload: updateData, status: !row.status, templateId: row._id });
    await refetchUserTemplates();
  };

  const handleActionItemClick = async (action, row) => {
    const updateData = { ...row };
    delete updateData?._id;
    delete updateData?.user;
    delete updateData?.status;
    delete updateData?.archive;
    delete updateData?.createdAt;
    delete updateData?.updatedAt;
    delete updateData?.__v;
    delete updateData?.clinicName;
    delete updateData?.clientResponses;
    await updateTemplate({ payload: updateData, archive: true, templateId: row._id });
    await refetchUserTemplates();
  };
  const finalRows = useMemo(() => {
    let filtered = userTemplates?.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    filtered = filtered?.filter((patient: any) => {
      if (searchString) {
        const properties = ['name', 'type', 'createdBy', 'status', 'createdOn'];

        let containsQuery = false;

        properties.forEach((property) => {
          if (patient[property]?.toString().toLowerCase().includes(searchString?.toString().toLowerCase())) {
            containsQuery = true;
          }
        });

        if (!containsQuery) {
          return false;
        }
      }

      return true;
    });

    return filtered;
  }, [searchString, userTemplates, page, rowsPerPage]);
  useEffect(() => {
    if (searchString) {
      setFormBuilderTemplates(finalRows || []);
    } else {
      setFormBuilderTemplates(userTemplates?.slice(page * rowsPerPage, (page + 1) * rowsPerPage) || []);
    }
  }, [rowsPerPage, page, userTemplates, finalRows, searchString, refetchUserTemplates]);

  const formBuilderTemplateColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'status',
        sortable: true,
        headerName: intl.formatMessage({ defaultMessage: 'Status' }),
        renderCell: (params: GridRenderCellParams) => {
          return <Switch defaultChecked={params.row.status} onClick={(_) => handleStatusToggle(params.row)} disabled={false} />;
        }
      },
      {
        field: 'name',
        sortable: true,
        headerName: intl.formatMessage({ defaultMessage: 'Template Name' }),
        valueGetter: (_, row: any) => {
          return row.name;
        }
      },
      {
        field: 'createdAt',
        sortable: true,
        headerName: intl.formatMessage({ defaultMessage: 'Created On' }),
        renderCell: (params: GridRenderCellParams) => {
          return <Typography>{params.row.createdAt ? moment(params.row.createdAt).format('MMM DD, YYYY') : ''}</Typography>;
        }
      },
      {
        field: 'type',
        sortable: true,
        headerName: intl.formatMessage({ defaultMessage: 'Type' }),
        valueGetter: (_, row: any) => {
          return row.type;
        }
      },
      {
        field: 'user.firstName',
        sortable: false,
        headerName: intl.formatMessage({ defaultMessage: 'Created By' }),
        valueGetter: (_, row: any) => {
          return row.user.firstName + ' ' + row.user.lastName;
        }
      },
      {
        field: 'actions',
        headerName: intl.formatMessage({ defaultMessage: 'Actions' }),
        width: 100,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box display="flex" alignItems="center">
              <Box display="flex" alignItems="center" gap={1}>
                <IconEye />
                <IconPencil onClick={(e) => handleAdd(e, params.row)} />
              </Box>

              <PopupState variant="popover" popupId="demo-popup-popover">
                {(popupState) => (
                  <>
                    <IconButton aria-label="more" id="long-button" {...bindTrigger(popupState)}>
                      <MoreVertIcon color="action" />
                    </IconButton>
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                      }}
                    >
                      {actions?.map((action, index: number) => (
                        <Typography
                          key={index}
                          sx={{
                            padding: '10px 16px 10px 16px',
                            borderBottom: index !== actions.length - 1 ? '1px solid black' : 'transparent',
                            cursor: 'pointer',
                            '&:hover': {
                              background: '#337AB7',
                              color: 'white',
                              cursor: 'pointer'
                            }
                          }}
                          onClick={(_: React.SyntheticEvent) => handleActionItemClick(action, params)}
                        >
                          {action}
                        </Typography>
                      ))}
                    </Popover>
                  </>
                )}
              </PopupState>
            </Box>
          );
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, navigate, userTemplates]
  );
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchString(value);
  };
  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  return (
    <>
      {userTemplatesIsLoading && userTemplates ? (
        <PopularCard />
      ) : (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }} px={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <GridSearchIcon fontSize="small" />{' '}
                    </InputAdornment>
                  )
                }}
                onChange={handleSearchChange}
                placeholder={'Search by Name...'}
                value={searchString}
                size="small"
                sx={{ flex: 1, width: 200 }}
              />
            </Box>

            <Box sx={{ display: 'flex', ml: 'auto' }}>
              <Button variant="contained" size="small" color="primary" onClick={(e) => handleAdd(e, null)} sx={{ p: '10px' }}>
                <AddIcon fontSize="small" sx={{ mr: 0.75 }} />
                <Typography variant="button" sx={{ textTransform: 'capitalize' }}>
                  {intl.formatMessage({ defaultMessage: 'Create Template' })}
                </Typography>
              </Button>
            </Box>
          </Box>
          <TableContainer className={classes.container}>
            <Table sx={{ tableLayout: 'fixed' }} stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {formBuilderTemplateColumns.map((column) => (
                    <TableCell
                      sx={{ py: 2 }}
                      key={column.field}
                      align={column.align}
                      style={{
                        minWidth: column.minWidth,
                        width: column.width,
                        whiteSpace: 'nowrap',
                        fontSize: 12
                      }}
                      sortDirection={orderBy === column.field ? order : false}
                    >
                      {column.sortable ? (
                        <TableSortLabel
                          active={orderBy === column.field}
                          direction={orderBy === column.field ? order : 'asc'}
                          onClick={createSortHandler(column.field)}
                        >
                          {column.headerName}
                          {orderBy === column.field ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      ) : (
                        <Box>{column.headerName}</Box>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {formBuilderTemplates && formBuilderTemplates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={formBuilderTemplateColumns.length}>
                      <div style={{ marginTop: 20, marginLeft: 10, marginRight: 10, width: '100%' }}>
                        <NoResults>
                          <FormattedMessage defaultMessage="No Results found." />
                        </NoResults>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {formBuilderTemplates?.sort(getComparator(order, orderBy))?.map((row: any) => {
                  return (
                    <TableRow key={row._id}>
                      <TableCell>
                        <Switch
                          defaultChecked={row.status}
                          onClick={(_) => handleStatusToggle(row)}
                          disabled={false}
                          size={matchDownSM ? 'small' : 'medium'}
                        />
                      </TableCell>
                      <TableCell>{row?.name}</TableCell>
                      <TableCell>{row?.createdAt ? moment(row?.createdAt).format('MMM DD, YYYY') : ''}</TableCell>
                      <TableCell>{row?.type === 'stepper' ? 'Wizard' : 'Default'}</TableCell>
                      <TableCell>{row?.user?.firstName + ' ' + row?.user?.lastName}</TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end" width={matchDownSM ? '92px' : 'auto'}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <IconEye
                              cursor="pointer"
                              onClick={() => {
                                showModal(PreviewFormBuilder, {
                                  type: row.type,
                                  steps: row.type === 'default' ? row.items : row.steps,
                                  defaultTypeTitle: row.type === 'default' && row.name,
                                  defaultTypeTitleFrench: row.type === 'default' && row.nameFrench,
                                  title: row?.name,
                                  titleFrench: row?.nameFrench,
                                  description: row?.description,
                                  descriptionFrench: row?.descriptionFrench,
                                  isAdminPreview: true
                                });
                              }}
                            />
                            <IconPencil onClick={(e) => handleAdd(e, row)} cursor="pointer" />
                          </Box>

                          <PopupState variant="popover" popupId="demo-popup-popover">
                            {(popupState) => (
                              <>
                                <IconButton aria-label="more" id="long-button" {...bindTrigger(popupState)}>
                                  <MoreVertIcon color="action" />
                                </IconButton>
                                <Popover
                                  {...bindPopover(popupState)}
                                  anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center'
                                  }}
                                  transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center'
                                  }}
                                >
                                  {actions?.map((action, index: number) => (
                                    <Typography
                                      key={index}
                                      sx={{
                                        padding: '10px 16px 10px 16px',
                                        borderBottom: index !== actions.length - 1 ? '1px solid black' : 'transparent',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          background: '#337AB7',
                                          color: 'white',
                                          cursor: 'pointer'
                                        }
                                      }}
                                      onClick={(_: React.SyntheticEvent) => handleActionItemClick(action, row)}
                                    >
                                      {action}
                                    </Typography>
                                  ))}
                                </Popover>
                              </>
                            )}
                          </PopupState>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 15]}
            component="div"
            count={userTemplates?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      )}
    </>
  );
};

export default TemplatesTable;
